/**
 * MemberTransferDialog — move a member to another assembly.
 *
 * The member keeps their profile, giving history and roles; only the church
 * they belong to changes. Every move is recorded with its date, origin and
 * destination and shown back here as history.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowRightLeft, Loader2, MapPin, Search } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Something went wrong (${res.status})`);
  return data;
};

export const MemberTransferDialog = ({ churchId, member, onClose, onTransferred, fromChurchName }) => {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');

  const { data: destinations, isLoading } = useQuery({
    queryKey: ['transfer-destinations', churchId],
    queryFn: () => call(`/api/church/${churchId}/transfer-destinations`),
    enabled: !!churchId,
  });

  const { data: history } = useQuery({
    queryKey: ['member-transfers', member?.id],
    queryFn: () => call(`/api/church/${churchId}/members/${member.id}/transfers`),
    enabled: !!member?.id,
  });

  const matches = useMemo(() => {
    const rows = destinations?.destinations || [];
    const needle = query.trim().toLowerCase();
    if (!needle) return rows.slice(0, 25);
    return rows.filter((r) => `${r.name} ${r.city} ${r.country}`.toLowerCase()
      .includes(needle)).slice(0, 25);
  }, [destinations, query]);

  const transfer = useMutation({
    mutationFn: () => call(`/api/church/${churchId}/members/${member.id}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ to_church_id: target.id, reason }),
    }),
    onSuccess: (data) => {
      toast.success(`${data.member?.first_name || 'Member'} transferred to ${data.to_church_name}`);
      qc.invalidateQueries({ queryKey: ['church-members', churchId] });
      qc.invalidateQueries({ queryKey: ['member-transfers', member.id] });
      onTransferred?.(data);
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  if (!member) return null;
  const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim();

  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto"
                     data-testid="member-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-emerald-700" /> Transfer {fullName}
          </DialogTitle>
          <DialogDescription data-testid="transfer-member-context">
            {[fromChurchName && `Currently at ${fromChurchName}`,
              member.role && `role: ${member.role}`,
              member.email || member.phone].filter(Boolean).join(' · ')}
            <br />
            They keep their profile, their giving history and the roles they hold — only
            the assembly they belong to changes. Their home group does not travel with them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Transfer to</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Search an assembly by name, city or country"
                     className="pl-8" data-testid="transfer-search" />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 divide-y">
              {isLoading && (
                <p className="p-4 text-sm text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" />Loading assemblies…
                </p>
              )}
              {!isLoading && matches.length === 0 && (
                <p className="p-4 text-sm text-slate-400" data-testid="transfer-no-destinations">
                  No other assembly matches that.
                </p>
              )}
              {matches.map((row) => (
                <button key={row.id} type="button" onClick={() => setTarget(row)}
                        data-testid={`transfer-destination-${row.id}`}
                        className={`w-full text-left px-3 py-2.5 text-sm transition-colors
                          ${target?.id === row.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                  <span className="font-medium text-slate-800">{row.name}</span>
                  {(row.city || row.country) && (
                    <span className="ml-2 text-xs text-slate-500 inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[row.city, row.country].filter(Boolean).join(', ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Relocating for work, marriage, church planting…"
                      data-testid="transfer-reason" />
          </div>

          {(history?.transfers || []).length > 0 && (
            <div className="rounded-md border border-slate-200 p-3" data-testid="transfer-history">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Transfer history
              </p>
              <ul className="mt-2 space-y-2">
                {history.transfers.map((t) => (
                  <li key={t.id} className="text-xs text-slate-600"
                      data-testid={`transfer-history-row-${t.id}`}>
                    <span className="font-medium text-slate-800">
                      {new Date(t.transferred_at).toLocaleDateString()}
                    </span>
                    {' — '}{t.from_church_name || 'unknown'} → {t.to_church_name || 'unknown'}
                    {t.carried_roles?.length > 0 && (
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        kept {t.carried_roles.join(', ')}
                      </Badge>
                    )}
                    {t.reason ? <span className="block text-slate-400">{t.reason}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="transfer-cancel">Cancel</Button>
            <Button disabled={!target || transfer.isPending} onClick={() => transfer.mutate()}
                    data-testid="transfer-confirm">
              {transfer.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {target ? `Transfer to ${target.name}` : 'Choose an assembly'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MemberTransferDialog;
